export type CodeSetSelectorScope = {
    identity: string;
    description: string;
};

export type CodeSetSelectorOption = {
    name: string;
    description: string;
    scopes: CodeSetSelectorScope[];
};

type CodeSetSource = {
    name: string;
    description: string | null;
    scopes: string[];
};

// A fixed locale, so the order is the same in every browser and test run. Under nb-NO, "AA" collates as "Å" and sorts last.
const collator = new Intl.Collator('en');

function byIdentity(a: CodeSetSelectorScope, b: CodeSetSelectorScope): number {
    return collator.compare(a.identity, b.identity);
}

export function scopeLabel(option: CodeSetSelectorOption): string {
    return option.scopes.map(scope => scope.identity).join(', ');
}

/**
 * Builds one selectable option per codeset, with the codeset's scopes grouped on it.
 * The source codeset is left out so that it can never be selected as a copy target.
 */
export function buildCodeSetOptions(
    codeSets: CodeSetSource[],
    scopes: CodeSetSelectorScope[],
    sourceCodeSetName: string
): CodeSetSelectorOption[] {
    const scopeDescriptions = new Map(scopes.map(scope => [scope.identity, scope.description]));
    const source = sourceCodeSetName.toLowerCase();

    return codeSets
        .filter(codeSet => codeSet.name.toLowerCase() !== source)
        .map(codeSet => ({
            name: codeSet.name,
            description: codeSet.description ?? '',
            scopes: codeSet.scopes
                .map(identity => ({ identity, description: scopeDescriptions.get(identity) ?? '' }))
                .sort(byIdentity)
        }))
        .sort((a, b) => collator.compare(scopeLabel(a) || a.name, scopeLabel(b) || b.name));
}

export function filterCodeSetOptions(options: CodeSetSelectorOption[], searchText: string): CodeSetSelectorOption[] {
    const search = searchText.trim().toLowerCase();
    if (search === '')
        return options;

    return options.filter(option =>
        [option.name, option.description, ...option.scopes.flatMap(scope => [scope.identity, scope.description])]
            .some(text => text.toLowerCase().includes(search)));
}

/** Names of the options that hold at least one of the given scopes. */
export function codeSetNamesForScopes(options: CodeSetSelectorOption[], scopeIdentities: string[]): string[] {
    const wanted = new Set(scopeIdentities);
    return options
        .filter(option => option.scopes.some(scope => wanted.has(scope.identity)))
        .map(option => option.name);
}
